import { ContactField } from "../hooks/useFieldDictionary";

export const IGNORE_TARGET = "";

/**
 * Client-side mapping validators that mirror the backend contract exactly
 * (backend/app/services/import_field_dict.py). If backend adds/removes a
 * validator, this file changes with it — the backend response remains the
 * authoritative gate.
 */

export function requiredFields(fields: ContactField[]): string[] {
  return fields.filter((f) => f.required).map((f) => f.code);
}

export function unmappedRequired(
  mapping: Record<string, string>,
  fields: ContactField[],
): string[] {
  const mappedTargets = new Set(
    Object.values(mapping).filter((v) => v && v !== IGNORE_TARGET),
  );
  return requiredFields(fields).filter((code) => !mappedTargets.has(code));
}

export function duplicateTargets(mapping: Record<string, string>): string[] {
  const counts = new Map<string, number>();
  for (const v of Object.values(mapping)) {
    if (!v || v === IGNORE_TARGET) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return [...counts.entries()]
    .filter(([, n]) => n > 1)
    .map(([t]) => t)
    .sort();
}

export function unknownTargets(
  mapping: Record<string, string>,
  fields: ContactField[],
): string[] {
  const known = new Set(fields.map((f) => f.code));
  return [
    ...new Set(
      Object.values(mapping).filter(
        (v) => v && v !== IGNORE_TARGET && !known.has(v),
      ),
    ),
  ].sort();
}

export function labelFor(target: string, fields: ContactField[]): string {
  if (!target || target === IGNORE_TARGET) return "— Ignore column —";
  return fields.find((f) => f.code === target)?.label ?? target;
}

/** Extract server-side error details when POST /mapping returns 400. */
export function parseMappingError(err: unknown): {
  code: string;
  targets?: string[];
  fields?: string[];
  unknown?: string[];
  message?: string;
} | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    return detail as {
      code: string;
      targets?: string[];
      fields?: string[];
      unknown?: string[];
    };
  }
  if (typeof detail === "string") return { code: detail };
  return null;
}
